import { Alert, Button, Drawer, Dropdown, Input, Modal, Radio } from "antd";
import "./EditScreenContentModal.scss";
import React, { ReactNode, useContext, useEffect, useState } from "react";
import { SearchOutlined, QuestionCircleOutlined, LeftOutlined, CloseOutlined, WarningOutlined, ClockCircleOutlined } from "@ant-design/icons";
import DSAApi from "../../api/DSAApi";
import {DsaPlaylistDto, DsaScheduleDto, DsaScreenDto} from "../../generated/dashboard-client";
import SavedDesignsPicker, { designKey } from "./SavedDesignsPicker";
import { getS3Url } from "../../util/utils";
import MediaThumb from "./MediaThumb";
import { memberState, locationsState, mobileState } from "../../state/atoms";
import { useRecoilState, useRecoilValue } from "recoil";
import PlaylistIcon from "../icons/PlaylistIcon";
import { NavLink } from "react-router-dom";
import { env } from "../../env/env";
import PaintbrushIcon from "../icons/content-pages/PaintbrushIcon";
import { AppContext } from "../../AppContext";
import LocationIconRound from "../icons/LocationIconRound";
import ScheduleIcon from "../icons/ScheduleIcon";
import moment from "moment-timezone";
import { eventWallClock, wallClockMillis } from "../../service/dsaEventTime";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";
import UploadIcon from "../icons/UploadIcon";
import { Spinner } from "../account/misc/Spinner";
import { getPlaylistImage } from "./PlaylistCard";
import UploadDisplayImage from "./UploadDisplayImage";
import QRDisplayUploadsApi from "../../api/QRDisplayUploadsApi";
import { rrulestr } from "rrule";
import PublishRecentChangesModal from "./PublishRecentChangesModal";
import { usePublishDesign } from "./usePublishDesign";

export interface EditScreenContentModalProps {
  id: string | undefined;
  locationId: number | undefined;
  isVisible: boolean;
  withTooltip: (tooltip: string, object: ReactNode) => ReactNode;
  onClose: () => void;
  onDesignSelect: (design: DesignView) => void;
  onPlaylistSelect: (playlist: DsaPlaylistDto) => void;
  onScheduleSelect: (schedule: DsaScheduleDto) => void;
  onUploadSelect: (upload: DisplayUploadDto) => void;
  openMobileMenu: () => void;
  screen: DsaScreenDto | undefined;
}

enum View { One, Two }
enum Type { Board, Playlist, Schedule, Upload }

export interface DesignView {
  id: string;
  size: string;
  page: number;
  name: string;
  thumbnail: string;
  selected: boolean;
  visible: boolean;
  width?: string;
  height?: string;
  unit?: string;
  locationId?: number;
  hasUnpublishedChanges: boolean;
}

export interface SearchablePlaylist extends DsaPlaylistDto {
  visible?: boolean;
  selected: boolean;
}

export interface SearchableSchedule extends DsaScheduleDto {
  visible?: boolean;
  selected: boolean;
}

export interface SearchableUpload extends DisplayUploadDto {
  visible?: boolean;
  selected?: boolean;
}

export function EditScreenContentModal({ id, screen, locationId, isVisible, withTooltip, onClose, onDesignSelect, onPlaylistSelect, onScheduleSelect, onUploadSelect, openMobileMenu }: EditScreenContentModalProps) {
  const { location } = useContext(AppContext);
  const [locations] = useRecoilState(locationsState);

  const [view, setView] = useState(View.One as View);
  const [type, setType] = useState(Type.Board as Type);
  const [selectedDesign, setSelectedDesign] = useState(null as DesignView | null);
  // const [, setBoards] = useState([] as DsaBoardDto[]);
  const [playlists, setPlaylists] = useState([] as SearchablePlaylist[]);
  const [schedules, setSchedules] = useState([] as SearchableSchedule[]);
  const [uploads, setUploads] = useState([] as SearchableUpload[]);
  const [search, setSearch] = useState("");
  const [member] = useRecoilState(memberState);
  const [filterLocation, setFilterLocation] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [publishPrompt, setPublishPrompt] = useState(false);
  const { publish } = usePublishDesign();

  const mobile = useRecoilValue(mobileState);

  useEffect(() => {
    setFilterLocation(location.internalId!);
    setSearch('');
    DSAApi.getPlaylists().then(resp => {
      const pls = resp.data as SearchablePlaylist[];
      pls.forEach(it => {
        it.visible = it.locationId === location.internalId;
      })
      setPlaylists(pls.sort((a, b) => { return Date.parse(b.updatedDate!) - Date.parse(a.updatedDate!); }));
    });
    DSAApi.getSchedules().then(resp => {
      const schedules = resp.data as SearchableSchedule[];
      schedules.forEach(it => {
        it.visible = it.locationId === location.internalId;
      })
      setSchedules(schedules.sort((a, b) => { return Date.parse(b.updatedDate!) - Date.parse(a.updatedDate!); }));
    });
    QRDisplayUploadsApi.getDisplayUploadsByLocationIds(locations.map(l => l.internalId!)).then(resp => {
      const dus = resp.data as SearchableUpload[];
      dus.forEach(it => {
        it.visible = it.locationId === location.internalId;
      })
      setUploads(dus);
    });
  }, [member.internalId, location.internalId, locations]);

  useEffect(() => {
    setView(View.One);
    setSelectedDesign(null);
    setPublishPrompt(false);
  }, [isVisible]);

  function searchPlaylists(key: string, location: number) {
    setFilterLocation(location);
    setSearch(key);
    playlists.forEach(function (it) {
      it.visible = key ? (it.name?.toLowerCase()?.indexOf(key.toLowerCase()) || 0) > -1 && it.locationId === location : it.locationId === location;
    });
    setPlaylists(playlists);
  }

  function searchSchedules(key: string, location: number) {
    setFilterLocation(location);
    setSearch(key);
    schedules.forEach(function (it) {
      it.visible = key ? (it.name?.toLowerCase()?.indexOf(key.toLowerCase()) || 0) > -1 && it.locationId === location : it.locationId === location;
    });
    setSchedules(schedules);
  }

  function searchUploads(key: string, location: number) {
    setFilterLocation(location);
    setSearch(key);
    uploads.forEach(function (it) {
      it.visible = key ? (it.name?.toLowerCase()?.indexOf(key.toLowerCase()) || 0) > -1 && it.locationId === location : it.locationId === location;
    });
    setUploads(uploads);
  }

  function selectDesignFlow() {
    setView(View.Two);
    setType(Type.Board);
  }

  function selectPlaylistFlow() {
    setView(View.Two);
    setType(Type.Playlist);
  }

  function selectScheduleFlow() {
    setView(View.Two);
    setType(Type.Schedule);
  }

  function selectUploadFlow() {
    setView(View.Two);
    setType(Type.Upload);
  }

  function selectPlaylist(playlist: SearchablePlaylist) {
    playlists.forEach(it => it.selected = false);
    playlist.selected = true;
    setPlaylists([...playlists]);
  }

  function selectSchedule(schedule: SearchableSchedule) {
    schedules.forEach(it => it.selected = false);
    schedule.selected = true;
    setSchedules([...schedules]);
  }

  function selectPlaylistDone() {
    const toSelect = playlists.find(it => it.selected);
    if (toSelect) {
      onPlaylistSelect(toSelect);
    }
  }

  function selectScheduleDone() {
    const toSelect = schedules.find(it => it.selected);
    if (toSelect) {
      onScheduleSelect(toSelect);
    }
  }

  function selectUpload(upload: SearchableUpload) {
    uploads.forEach(it => it.selected = false);
    upload.selected = true;
    setUploads([...uploads]);
  }

  function selectUploadDone() {
    const toSelect = uploads.find(it => it.selected);
    if (toSelect) {
      onUploadSelect(toSelect);
    }
  }

  function onBackClick() {
    openMobileMenu();
    onClose();
  }

  function onAssignClick() {
    if (!selectedDesign) return;
    if (selectedDesign.hasUnpublishedChanges) {
      setPublishPrompt(true);
      return;
    }
    onDesignSelect(selectedDesign);
  }

  function onPublishThenAssign() {
    const design = selectedDesign;
    setPublishPrompt(false);
    if (!design) return;
    publish(design.id);
    onDesignSelect({ ...design, hasUnpublishedChanges: false });
  }

  function onSkipPublish() {
    const design = selectedDesign;
    setPublishPrompt(false);
    if (!design) return;
    onDesignSelect(design);
  }

  function getScheduleDateInfo(sched: SearchableSchedule) {
    const firstEvent = sched.events?.sort((a, b) => {
      return a.startDate! - b.startDate!;
    })![0]!;
    const startDateFormatted = `${eventWallClock(firstEvent).start.format('MM/DD/YYYY')}, ${moment(firstEvent.startTime, 'HH:mm').format('h:mma')}`;

    const maxInt = '2147483647';
    // every date below is held as a naive wall clock, which lines 'start'/'end' up with the screen
    // timezone they are re-read in further down
    let lastEventDate = moment.utc(0);
    let infinite = false;
    for (let i = 0; i < sched.events!.length; i++) {
      const thisEvent = sched.events![i];
      const wallClock = eventWallClock(thisEvent);
      if (thisEvent.repeat && thisEvent.repeat !== 'no-repeat' && thisEvent.rrule) {
        if (thisEvent.rrule.indexOf(maxInt) > -1) {
          infinite = true;
          break;
        } else {
          const startMs = wallClockMillis(wallClock.start);
          const duration = wallClockMillis(wallClock.end) - startMs;
          const rrule = rrulestr(`DTSTART:${moment.utc(startMs).format('YYYYMMDDTHHmmss')}\nRRULE:${thisEvent.rrule}`, { forceset:true });
          const occurrences = rrule.all();
          const potentialLastDate = moment.utc(occurrences[occurrences.length - 1]).add(duration, 'milliseconds');
          if (potentialLastDate > lastEventDate) {
            lastEventDate = potentialLastDate;
          }
        }
      } else {
        const potentialLastDate = moment.utc(wallClockMillis(wallClock.end));
        if (potentialLastDate > lastEventDate) {
          lastEventDate = potentialLastDate;
        }
      }
    }
    const endDateFormatted = `${lastEventDate.format('MM/DD/YYYY, h:mma')}`;

    const tz = screen?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const start = moment.tz(startDateFormatted, 'MM/DD/YYYY, h:mma', tz);
    const end = moment.tz(endDateFormatted, 'MM/DD/YYYY, h:mma', tz);
    const now = moment().tz(tz);

    const status = now.isBefore(start) ? 'future' : infinite ? 'present' : now.isBetween(start, end) ? 'present' : end.isBefore(now) ? 'past' : '';

    return {
      startDateFormatted: startDateFormatted,
      endDateFormatted: infinite ? 'Never' : endDateFormatted,
      status: status,
    };
  }

  const screenLocationId = screen?.locationId;
  const crossLocation = !!selectedDesign && selectedDesign.locationId != null && screenLocationId != null && selectedDesign.locationId !== screenLocationId;
  const locationName = (id?: number) => locations.find(l => l.internalId === id)?.name ?? "";

  const content = (<>
    {view === View.One && <div className={"select"}>
      <header>
        {mobile && <div className="back-btn" onClick={() => onBackClick()}><LeftOutlined /></div>}
        <h3>Add Content to Your Screen</h3>
        <div className="close-btn" onClick={() => onClose()}><CloseOutlined /></div>
      </header>
      <div className={"buttons"}>
        <Button onClick={selectDesignFlow}>
          <PaintbrushIcon />
          Saved Designs
        </Button>
        <Button onClick={selectPlaylistFlow}>
          <PlaylistIcon />
          Playlists
        </Button>
        <Button onClick={selectScheduleFlow}>
          <ScheduleIcon />
          Schedules
        </Button>
        <Button onClick={selectUploadFlow}>
          <UploadIcon />
          Uploads
        </Button>
      </div>
      <div className="help-btn-cont">
        <a href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029524-displaying-content" target="_blank" rel="noreferrer">
          <QuestionCircleOutlined />
          Help
        </a>
      </div>
    </div>}
    {view === View.Two && type === Type.Board && <div className={"select"}>
      <header>
        <div className="back-btn" onClick={() => setView(View.One)}><LeftOutlined /></div>
        <div className="close-btn" onClick={() => onClose()}><CloseOutlined /></div>
        <h3>Select a Saved Design</h3>
      </header>
      <SavedDesignsPicker
        locations={locations}
        defaultLocationId={screen?.locationId ?? location.internalId!}
        currentLocationId={screen?.locationId ?? location.internalId!}
        currentLocationBadgeLabel="This screen's location"
        selectedId={selectedDesign ? designKey(selectedDesign) : null}
        onSelect={setSelectedDesign}
        emptyState={<>
          <PaintbrushIcon color="#cfcfcf" />
          <p>You don't have any designs yet.</p>
          <NavLink to={`${env.dashboardPath}/designs`} className={`ant-btn ant-btn-primary`}>Create a design</NavLink>
        </>}
      />
      {crossLocation &&
        <Alert className="cross-location-notice" type="warning" showIcon
          message={<>You're assigning a design from <b>{locationName(selectedDesign?.locationId)}</b> to a screen at <b>{locationName(screen?.locationId)}</b>.</>} />}
      <footer>
        {crossLocation
          ? <>
            <div className="ant-btn ant-btn-lg" onClick={() => setSelectedDesign(null)}>Cancel</div>
            <div className="ant-btn ant-btn-primary ant-btn-lg" onClick={() => onAssignClick()}>Assign anyway</div>
          </>
          : <div className={`ant-btn ant-btn-primary ant-btn-lg ${!selectedDesign ? 'disabled' : ''}`}
            onClick={() => onAssignClick()}>Assign</div>}
        {withTooltip("Help", <a className="help-btn" href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029524-displaying-content" target="_blank" rel="noreferrer">
          <QuestionCircleOutlined />
        </a>)}
      </footer>
    </div>}
    {view === View.Two && type === Type.Playlist && <div className={"select"}>
      <header>
        <div className="back-btn" onClick={() => setView(View.One)}><LeftOutlined /></div>
        <div className="close-btn" onClick={() => onClose()}><CloseOutlined /></div>
        <h3>Select a Playlist</h3>
        <span className="search-filter">
          <Input type="search" prefix={<SearchOutlined />} placeholder="Search by playlist name"
            value={search} onChange={ev => searchPlaylists(ev.target.value, filterLocation)} />
          <Dropdown trigger={['click']} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement} placement="bottomRight"
            overlay={<div className="design-locs">
              <p>Locations</p>
              <Radio.Group onChange={(e) => searchPlaylists('', e.target.value)} value={filterLocation}>
                {locations.map(loc => {
                  return <Radio value={loc.internalId!}>{loc.name}</Radio>
                })}
              </Radio.Group>
            </div>}
          >
            {withTooltip("Location", <div className="loc-filter">
              <LocationIconRound />
            </div>)}
          </Dropdown>
          <span className="cur-loc">
            <b>Location:</b> {locations.find(loc => loc.internalId === filterLocation)!.name}
          </span>
        </span>
      </header>
      {((playlists.filter(it => it.visible).filter(it => !!it.entries?.length).length === 0 && search.length === 0) || playlists.length === 0) &&
        <div className="none-yet">
          <PlaylistIcon />
          <p>You don't have any playlists yet.</p>
          <NavLink to={`${env.dashboardPath}/dsa/playlists`} className={`ant-btn ant-btn-primary`}>Create a playlist</NavLink>
        </div>}
      {playlists.length > 0 && playlists.filter(it => it.visible).filter(it => !!it.entries?.length).length === 0 && search.length > 0 &&
        <div className="none-yet">No results.</div>}
      {playlists.length > 0 &&
        <ul className={"designs"}>
          {playlists
            .filter(it => it.visible)
            .filter(it => !!it.entries?.length)
            .map(it => <li className={`design ${it.selected ? 'sel' : ''}`} key={`${it.id}`} onClick={() => selectPlaylist(it)}>
              <MediaThumb src={getPlaylistImage(it, uploads)} alt={it.name} />
              <h5>{it.name}</h5>
            </li>)}
        </ul>}
      <footer>
        <div className={`ant-btn ant-btn-primary ant-btn-lg ${playlists.filter(it => it.selected).length === 0 ? 'disabled' : ''}`}
          onClick={() => selectPlaylistDone()}>Done</div>
        {withTooltip("Help", <a className="help-btn" href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029524-displaying-content" target="_blank" rel="noreferrer">
          <QuestionCircleOutlined />
        </a>)}
      </footer>
    </div>}
    {view === View.Two && type === Type.Schedule && <div className={"select"}>
      <header>
        <div className="back-btn" onClick={() => setView(View.One)}><LeftOutlined /></div>
        <div className="close-btn" onClick={() => onClose()}><CloseOutlined /></div>
        <h3>Select a Schedule</h3>
        <span className="search-filter">
          <Input type="search" prefix={<SearchOutlined />} placeholder="Search by schedule name"
            value={search} onChange={ev => searchSchedules(ev.target.value, filterLocation)} />
          <Dropdown trigger={['click']} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement} placement="bottomRight"
            overlay={<div className="design-locs">
              <p>Locations</p>
              <Radio.Group onChange={(e) => searchSchedules('', e.target.value)} value={filterLocation}>
                {locations.map(loc => {
                  return <Radio value={loc.internalId!}>{loc.name}</Radio>
                })}
              </Radio.Group>
            </div>}
          >
            {withTooltip("Location", <div className="loc-filter">
              <LocationIconRound />
            </div>)}
          </Dropdown>
          <span className="cur-loc">
            <b>Location:</b> {locations.find(loc => loc.internalId === filterLocation)!.name}
          </span>
        </span>
      </header>
      {((schedules.filter(it => it.visible).filter(it => !!it.events?.length).length === 0 && search.length === 0) || schedules.length === 0) &&
        <div className="none-yet">
          <ScheduleIcon />
          <p>You don't have any schedules yet.</p>
          <NavLink to={`${env.dashboardPath}/dsa/schedules`} className={`ant-btn ant-btn-primary`}>Create a schedule</NavLink>
        </div>}
      {schedules.length > 0 && schedules.filter(it => it.visible).filter(it => !!it.events?.length).length === 0 && search.length > 0 &&
        <div className="none-yet">No results.</div>}
      {schedules.length > 0 &&
        <ul className={"designs schedules"}>
          {schedules
          .filter(it => it.visible)
          .filter(it => !!it.events?.length)
          .map(it => {
            const scheduleDateInfo = getScheduleDateInfo(it);
            return <li className={`design ${it.selected ? 'sel' : ''}`} key={`${it.id}`}>
              <div className="clickable-area" onClick={() => selectSchedule(it)}>
                <h5>{it.name}</h5>
                {!mobile &&
                <span><b>Start:</b> {scheduleDateInfo.startDateFormatted} | <b>End:</b> {scheduleDateInfo.endDateFormatted}</span>}
              </div>
              <div className="status-icon">
                {scheduleDateInfo.status === 'past' && withTooltip('Outdated', <WarningOutlined style={{color: "#FF4D4F"}}/>)}
                {scheduleDateInfo.status === 'present' && withTooltip('Current', <ClockCircleOutlined style={{color: "#52C41A"}}/>)}
                {scheduleDateInfo.status === 'future' && withTooltip('Upcoming', <img src="https://timber.mhmcdn.com/site/marketing/icons/ScheduleUpcoming.svg" alt="Upcoming schedule"/>)}
              </div>
            </li>}
          )}
        </ul>}
      <footer>
        <div className={`ant-btn ant-btn-primary ant-btn-lg ${schedules.filter(it => it.selected).length === 0 ? 'disabled' : ''}`}
          onClick={() => selectScheduleDone()}>Done</div>
        {withTooltip("Help", <a className="help-btn" href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029524-displaying-content" target="_blank" rel="noreferrer">
          <QuestionCircleOutlined />
        </a>)}
      </footer>
    </div>}

    {view === View.Two && type === Type.Upload && <div className={"select"}>
      <header>
        <div className="back-btn" onClick={() => setView(View.One)}><LeftOutlined /></div>
        <div className="close-btn" onClick={() => onClose()}><CloseOutlined /></div>
        <h3>Select an Upload</h3>
        <span className="search-filter">
          <Input type="search" prefix={<SearchOutlined />} placeholder="Search by design name"
            value={search} onChange={ev => searchUploads(ev.target.value, filterLocation)} />
          <Dropdown trigger={['click']} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement} placement="bottomRight"
            overlay={<div className="design-locs">
              <p>Locations</p>
              <Radio.Group onChange={(e) => searchUploads('', e.target.value)} value={filterLocation}>
                {locations.map(loc => {
                  return <Radio value={loc.internalId!} key={loc.internalId}>{loc.name}</Radio>
                })}
              </Radio.Group>
            </div>}
          >
            {withTooltip("Location", <div className="loc-filter">
              <LocationIconRound />
            </div>)}
          </Dropdown>
          <span className="cur-loc">
            <b>Location:</b> {locations.find(loc => loc.internalId === filterLocation)!.name}
          </span>
        </span>
      </header>
      {uploading && <div className="none-yet">
        <div className={'spinner'}><Spinner /></div>
      </div>}
      {!uploading && uploads.length === 0 && uploads.filter(it => it.visible).length === 0 &&
        <div className="none-yet">
          <img src="https://timber.mhmcdn.com/site/marketing/icons/Upload.svg" alt="Upload" />
          <p>You don't have any uploads yet.</p>
          <UploadDisplayImage
            beforeUpload={() => setUploading(true)}
            afterUpload={() => {
              setUploading(false);
            }}
            uploads={uploads}
            setUploads={setUploads}
          />
        </div>}
      {!uploading && uploads.length > 0 && uploads.filter(it => it.visible).length === 0 &&
        <div className="none-yet">No results.</div>}
      {!uploading && uploads.length > 0 && <ul className={"designs"}>
        {uploads
          .filter(it => it.visible)
          .map(it => <li className={`design ${it.selected ? 'sel' : ''}`} key={`${it.id}`} onClick={() => selectUpload(it)}>
            <MediaThumb src={getS3Url(it.path!)} alt={it.name} />
            <h5>{it.name}</h5>
          </li>)}
      </ul>}
      <footer>
        <div className={`ant-btn ant-btn-primary ant-btn-lg ${uploads.filter(it => it.selected).length === 0 ? 'disabled' : ''}`}
          onClick={() => selectUploadDone()}>Done</div>
        {withTooltip("Help", <a className="help-btn" href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029524-displaying-content" target="_blank" rel="noreferrer">
          <QuestionCircleOutlined />
        </a>)}
      </footer>
    </div>}
  </>
  )

  return (<>
    <PublishRecentChangesModal
      visible={publishPrompt}
      onSkip={onSkipPublish}
      onPublish={onPublishThenAssign}
      onClose={() => setPublishPrompt(false)}
    />
    {!mobile ?
    <Modal
      className={"clean manage-screens"}
      visible={isVisible}
      onCancel={() => onClose()}
    >
      {content}
    </Modal>
    :
    <Drawer
      placement={"bottom"}
      visible={isVisible}
      className={"mobile-bottom-menu manage-screens"}
      height={"80vh"}
      closeIcon={false}
      maskClosable={true}
      onClose={() => onClose()}
    >
      {content}
    </Drawer>}
  </>);
}
